import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DocumentMarkupControllerService } from '@app/generated/api';
import { DocumentMarkupDto } from '@app/generated/model';
import { AnnotationsByPage, StoredAnnotation } from '@app/work-order/pdf-markup/pdf-markup-annotation.model';

@Injectable({
  providedIn: 'root',
})
export class PdfMarkupService {
  constructor(private markupApi: DocumentMarkupControllerService) {}

  loadMarkups(documentId: string): Observable<AnnotationsByPage> {
    return this.markupApi.getMarkups(documentId).pipe(
      map((result) => this.extractMarkupArray(result)),
      map((dtos) => this.groupMarkupsByPage(dtos)),
    );
  }

  saveMarkups(documentId: string, byPage: AnnotationsByPage): Observable<AnnotationsByPage> {
    const dtos = this.toDtos(documentId, byPage);
    return this.markupApi
      .saveMarkups(documentId, dtos)
      .pipe(map((result) => this.groupMarkupsByPage(this.extractMarkupArray(result))));
  }

  deleteMarkup(documentId: string, markupId: string): Observable<unknown> {
    return this.markupApi.deleteMarkup(documentId, markupId);
  }

  private toDtos(documentId: string, byPage: AnnotationsByPage): DocumentMarkupDto[] {
    const dtos: DocumentMarkupDto[] = [];
    for (const [page, annotations] of Object.entries(byPage)) {
      for (const annotation of annotations) {
        dtos.push({
          id: annotation.id,
          documentId,
          pageNumber: Number(page),
          type: annotation.type,
          dataJson: annotation,
        });
      }
    }
    return dtos;
  }

  private extractMarkupArray(result: unknown): DocumentMarkupDto[] {
    if (Array.isArray(result)) return result as DocumentMarkupDto[];
    if (result && typeof result === 'object') {
      const candidateKeys = ['content', 'items', 'data', 'markups', 'results', 'value'];
      for (const key of candidateKeys) {
        const value = (result as Record<string, unknown>)[key];
        if (Array.isArray(value)) return value as DocumentMarkupDto[];
      }
      // Fallback: find the first array-valued property, whatever it's called.
      for (const value of Object.values(result as Record<string, unknown>)) {
        if (Array.isArray(value)) return value as DocumentMarkupDto[];
      }
    }
    console.warn('[PdfMarkupService] Unexpected markups response shape, no markups restored:', result);
    return [];
  }

  private toAnnotation(dto: DocumentMarkupDto): StoredAnnotation | null {
    try {
      const annotation =
        typeof dto.dataJson === 'string'
          ? (JSON.parse(dto.dataJson) as StoredAnnotation)
          : (dto.dataJson as StoredAnnotation);
      if (!annotation) return null;
      annotation.id = dto.id;
      return annotation;
    } catch (err) {
      console.warn('[PdfMarkupService] Skipping malformed markup dataJson:', err);
      return null;
    }
  }

  private groupMarkupsByPage(dtos: DocumentMarkupDto[]): AnnotationsByPage {
    const byPage: AnnotationsByPage = {};
    for (const dto of dtos) {
      if (dto.pageNumber == null) continue;
      const annotation = this.toAnnotation(dto);
      if (!annotation) continue;
      (byPage[dto.pageNumber] ??= []).push(annotation);
    }
    return byPage;
  }
}
